import React, { useEffect, useState } from "react";
import { Tag, Button, message, Modal, Descriptions, Divider, Empty } from "antd";
import { GlobalOutlined, QrcodeOutlined, ReloadOutlined, CalendarOutlined, DownloadOutlined, EyeOutlined } from "@ant-design/icons";
import { QRCodeCanvas } from "qrcode.react";
import axios from "axios";
import "./QRList.css";

const QRList = () => {
  const [data, setData]           = useState([]);
  const [loading, setLoading]     = useState(true);
  const [selectedQR, setSelectedQR] = useState(null);

  const fetchQRList = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token") || "";
      const res = await axios.get(`${process.env.REACT_APP_BASE_URL}/api/QR/link-to-qr`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(res.data.data || []);
    } catch { message.error("Failed to fetch QR list"); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchQRList(); }, []);

  const formatDate = (d) => {
    try { return new Date(d.split(".")[0]).toLocaleDateString("en-IN", { day:"numeric", month:"short", year:"numeric" }); }
    catch { return d; }
  };

  const handleDownload = (record) => {
    const canvas = document.getElementById(`qr-canvas-${record.id || record.title}`);
    if (canvas) {
      const url = canvas.toDataURL("image/png");
      const a = document.createElement("a");
      a.href = url; a.download = `${record.title || "qr"}.png`;
      a.click();
    }
  };

  return (
    <div className="qrl-root">
      <div className="qrl-header">
        <div>
          <div className="qrl-title">My QR Codes</div>
          <div className="qrl-sub">{data.length} QR code{data.length !== 1 ? "s" : ""} generated</div>
        </div>
        <Button icon={<ReloadOutlined />} onClick={fetchQRList} loading={loading} className="qrl-refresh">Refresh</Button>
      </div>

      {loading ? (
        <div className="qrl-skeleton">
          {[1,2,3].map((n) => <div key={n} className="qrl-skeleton-card" />)}
        </div>
      ) : data.length === 0 ? (
        <div className="qrl-empty">
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No QR codes yet" />
          <p className="qrl-empty-hint">Generate your first QR code using Link to QR or Text to QR.</p>
        </div>
      ) : (
        <div className="qrl-grid">
          {data.map((record, i) => {
            const isLink = Boolean(record.link);
            const qrValue = record.link || record.qrText || "";
            const id = record.id || record.title || i;
            return (
              <div key={id} className="qrl-card">
                <div className="qrl-card-preview" onClick={() => setSelectedQR(record)}>
                  <QRCodeCanvas
                    id={`qr-canvas-${id}`}
                    value={qrValue || "https://bonoxi.com"}
                    size={120}
                    fgColor="#1a1a2e"
                    bgColor="#ffffff"
                  />
                </div>
                <div className="qrl-card-body">
                  <div className="qrl-card-title">{record.title || "Untitled QR"}</div>
                  <div className="qrl-card-value">{qrValue.length > 40 ? qrValue.slice(0, 40) + "…" : qrValue}</div>
                  <div className="qrl-card-meta">
                    <Tag
                      icon={isLink ? <GlobalOutlined /> : <QrcodeOutlined />}
                      className={`qrl-type-tag ${isLink ? "link" : "text"}`}
                    >
                      {isLink ? "Link" : "Text"}
                    </Tag>
                    <span className="qrl-date"><CalendarOutlined /> {formatDate(record.createdAt)}</span>
                    <Tag color={record.status === "Active" ? "green" : "red"} style={{borderRadius:100,fontSize:11}}>{record.status}</Tag>
                  </div>
                </div>
                <div className="qrl-card-actions">
                  <Button size="small" icon={<EyeOutlined />} className="qrl-action-btn" onClick={() => setSelectedQR(record)} title="View" />
                  <Button size="small" icon={<DownloadOutlined />} className="qrl-action-btn" onClick={() => handleDownload(record)} title="Download PNG" />
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Detail Modal */}
      <Modal
        open={Boolean(selectedQR)}
        onCancel={() => setSelectedQR(null)}
        footer={null}
        width={480}
        title={<span style={{fontFamily:"Poppins",fontWeight:700}}>{selectedQR?.title || "QR Code"}</span>}
        centered
      >
        {selectedQR && (
          <div className="qrl-modal-body">
            <div className="qrl-modal-qr">
              <QRCodeCanvas
                value={selectedQR.link || selectedQR.qrText || "https://bonoxi.com"}
                size={220}
                fgColor="#1a1a2e"
                bgColor="#ffffff"
              />
            </div>
            <Divider />
            <Descriptions column={1} size="small" labelStyle={{fontWeight:600,color:"#888",fontSize:12}} contentStyle={{fontSize:13}}>
              <Descriptions.Item label="Type">{selectedQR.link ? "Link" : "Text"}</Descriptions.Item>
              <Descriptions.Item label="Status">{selectedQR.status}</Descriptions.Item>
              <Descriptions.Item label="Created">{formatDate(selectedQR.createdAt)}</Descriptions.Item>
              {selectedQR.link && (
                <Descriptions.Item label="URL">
                  <a href={selectedQR.link} target="_blank" rel="noopener noreferrer" style={{color:"#e5322d",wordBreak:"break-all"}}>{selectedQR.link}</a>
                </Descriptions.Item>
              )}
              {!selectedQR.link && selectedQR.qrText && (
                <Descriptions.Item label="Text">{selectedQR.qrText}</Descriptions.Item>
              )}
            </Descriptions>
            <Button
              type="primary"
              block
              icon={<DownloadOutlined />}
              style={{marginTop:16,borderRadius:10,height:42}}
              onClick={() => handleDownload(selectedQR)}
            >
              Download QR PNG
            </Button>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default QRList;
